import { LitElement, html } from 'lit';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { fs24Car, fs24Edit, fs24CarFilled } from '@cof/fs-icons';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { style } from './iconic-vehicle-card.style.js';
import { DebugService } from '../../../shared/services/debug.service.js';
import { VehicleService } from '../../../services/vehicle.service.js';
import { stratum } from '../../../../constants/stratum.js';
import { ApplicationService } from '../../../services/application.service.js';

FsIcon.add(fs24Car);
FsIcon.add(fs24CarFilled);
FsIcon.add(fs24Edit);

class RefiIconicVehicleCard extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicVehicleCard');
    this.selected = false;
    this.isMotorcycle = false;
    this.vehicleIndex = -1;
    this.vehicleId = '';
    this.ineligibleMessage = '';
    this.showButton = false;
    this.inline = false;
    this.inlineIneligible = false;
    this.canEdit = false;
    this.hideIneligibility = false;
  }

  static properties = {
    vehicle: { type: Object, attribute: true },
    showLicensePlate: { type: Boolean, attribute: true },
    selected: { type: Boolean, attribute: true },
    isMotorcycle: { type: Boolean },
    vehicleId: { type: String },
    vehicleIndex: { type: Number },
    ineligibleMessage: { type: String },
    showButton: { type: Boolean },
    inline: { type: Boolean },
    inlineIneligible: { type: Boolean, attribute: 'inline-ineligible' },
    canEdit: { type: Boolean },
    hideIneligibility: { type: Boolean, attribute: 'hide-ineligibility' },
  };

  static styles = [style];

  connectedCallback() {
    super.connectedCallback();
    this.vehicleId = `vehicleButton${this.vehicleIndex}`;
    if (this.showLicensePlate) {
      this.licensePlate = VehicleService.vehicleLookupModel.licensePlateNumber;
      this.licenseState =
        VehicleService.vehicleLookupModel.licensePlateStateCode;
    }

    if (this.isVehicleIneligible) {
      this.ineligibleMessage = VehicleService.getWarningText(
        this.vehicle.ineligibleReasons[0],
        this.vehicle.maxVehicleAge,
        'card'
      );
      this.isMotorcycle = this.vehicle.ineligibleReasons?.some(
        (x) => x === 'MOTORCYCLE'
      );
    }

    if (this.ineligibleMessage) {
      VehicleService.sendVehiclePrequalInfoToSDP(
        this.vehicle,
        false,
        this.ineligibleMessage
      );
    }
    this.isFullyIneligible =
      !this.hideIneligibility &&
      (this.isMotorcycle || this.isVehicleIneligible || this.inlineIneligible);

    if (ApplicationService.isInAppDeclineDelayOn) {
      if (!this.showButton) {
        stratum.publishTag(TagKey.ICONIC_VEHICLE_AND_LOAN_VEHICLE_SHOWN, {
          replacements: {
            value: `${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model}`,
          },
        });
      } else {
        stratum.publishTag(
          this.vehicle.isEligible
            ? TagKey.VEHICLE_FOUND_VEHICLE_FOUND_ELIGIBLE_2
            : TagKey.VEHICLE_FOUND_VEHICLE_FOUND_INELIGIBLE_2,
          {
            replacements: {
              value: `${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model}`,
            },
          }
        );
      }
    }
  }

  firstUpdated() {
    if (!ApplicationService.isInAppDeclineDelayOn) {
      if (this.isVehicleIneligible) {
        stratum.publishTag(TagKey.VEHICLE_FOUND_VEHICLE_FOUND_INELIGIBLE, {
          replacements: {
            value: `${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model}`,
          },
        });
      } else {
        stratum.publishTag(TagKey.VEHICLE_FOUND_VEHICLE_FOUND_ELIGIBLE, {
          replacements: {
            value: `${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model}`,
          },
        });
      }
    }
    this.isFullyIneligible =
      !this.hideIneligibility &&
      (this.isMotorcycle || this.isVehicleIneligible || this.inlineIneligible);
  }

  get isVehicleIneligible() {
    let isIneligible = false;
    if (this.vehicle.ineligibleReasons == null) {
      isIneligible = false;
    } else {
      isIneligible = this.vehicle.ineligibleReasons.length > 0;
    }
    return isIneligible;
  }

  // renderLicensePlate() {
  //   let licensePlateHTML = html``;
  //   if (this.showLicensePlate) {
  //     licensePlateHTML = html`
  //       <div class="vehicle-card__plate">
  //         <span class="vehicle-card__plate-state">${this.licenseState}</span>
  //         ${this.licensePlate}
  //       </div>
  //     `;
  //   }
  //   return licensePlateHTML;
  // }

  renderSelectedVehicleButton() {
    let selectedVehicleButtonHTML = html``;
    if (
      this.showButton &&
      (!this.isVehicleIneligible || this.hideIneligibility)
    ) {
      selectedVehicleButtonHTML = html` <fs-button
        id="${this.vehicleId}"
        name="${this.vehicleId}"
        delegated-aria-label="Use this ${this.vehicle.year} ${this.vehicle
          .make} ${this.vehicle.model}"
        variant="primary"
        @click="${this.selectThisVehicle}"
        class="card-button"
      >
        ${VehicleService.selectVehicleButtonCTA}
      </fs-button>`;
    }
    return selectedVehicleButtonHTML;
  }

  async selectThisVehicle() {
    this.debug.log('selected vehicle', this.vehicle);
    this.dispatchEvent(
      new CustomEvent('select-vehicle', {
        detail: {
          vehicle: this.vehicle,
        },
      })
    );
  }

  render() {
    this.debug.log('rendered');
    const cardDetails = html` <div class="flex" data-e2e-ref="vehicle-card">
      <fs-icon .disabled="${this.isFullyIneligible}" icon="fs-24-car"></fs-icon>
      <div class="vehicle-info">
        <fs-heading
          variant="${this.isFullyIneligible ? 'secondary' : 'primary'}"
          size="medium"
        >
          ${this.vehicle.year} ${this.vehicle.make}
          ${this.vehicle.model}</fs-heading
        >
        <fs-body-text
          variant="${this.isFullyIneligible ? 'disabled' : 'secondary'}"
          >${this.vehicle.vin}</fs-body-text
        >
        ${
          this.showLicensePlate
            ? html`
                <fs-body-text variant="secondary"
                  >${this.licenseState} ${this.licensePlate}</fs-body-text
                >
              `
            : ''
        }
      </div>
      ${
        this.selected
          ? html`<fs-label-badge compact="" variant="info" emphasis="medium"
              >Selected</fs-label-badge
            >`
          : ''
      }
      ${
        this.canEdit
          ? html`<fs-button
              variant="primary"
              type="button"
              icon-position="leading"
              delegated-aria-label="Edit this ${this.vehicle.year} ${this
                .vehicle.make} ${this.vehicle.model}"
              ghost
              compact
              @click="${this.editVehicle}"
              ><svg
                slot="button-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="m17.854 7.773-1.271 1.27-1.626-1.626 1.27-1.27a.502.502 0 0 1 .712-.001l.915.915a.505.505 0 0 1 0 .712ZM8.17 17.456l-2.033.407.407-2.033 7.706-7.705.813.813.812.813-7.705 7.705ZM18.56 6.354l-.914-.915a1.507 1.507 0 0 0-2.126 0l-1.624 1.625L5.73 15.23a.498.498 0 0 0-.137.255l-.583 2.917a.501.501 0 0 0 .588.588l2.917-.583a.505.505 0 0 0 .256-.137l8.166-8.166 1.625-1.624a1.508 1.508 0 0 0-.001-2.126Z"
                ></path>
              </svg>
              Edit</fs-button
            >`
          : ''
      }
      </div>
      ${
        !this.hideIneligibility &&
        (this.isMotorcycle ||
          this.isVehicleIneligible ||
          this.inlineIneligible) &&
        this.ineligibleMessage.length > 0
          ? html`<fs-helper-text type="error" class="helper-text"
              >${this.ineligibleMessage}</fs-helper-text
            >`
          : ''
      }
      ${this.renderSelectedVehicleButton()}
    </div>`;
    return html`
      ${this.inline
        ? cardDetails
        : html`<fs-container class="container card">
            ${cardDetails}
          </fs-container>`}
    `;
  }

  editVehicle() {
    stratum.publishTag(
      ApplicationService.isInAppDeclineDelayOn
        ? TagKey.ICONIC_VEHICLE_AND_LOAN_EDIT_VEHICLE_LINK
        : TagKey.ICONIC_VEHICLE_AND_LOAN_EDIT_CAR_BUTTON
    );
    this.dispatchEvent(
      new CustomEvent('go-to-vehicle', { bubbles: true, composed: true })
    );
  }
}

customElements.define('refi-iconic-vehicle-card', RefiIconicVehicleCard);
